import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { Provider } from 'modules/ats/lib/enums'
import { PromotionData } from 'modules/ats/store/models/Promotion'

import { CategoriesOptions, IndustriesOptions } from './LinkedInData'
import { StepEvent, StepParams } from './StepParams'
import type { VonqCampaignData, VonqData } from './Types'

const sorted = (options: SelectOption[]) => options.sort((a, b) => a.label.localeCompare(b.label))

class Errors {
  [key: string]: Array<string> | Record<string, never>
}

const LINKEDIN_CHANNEL_ID = 66

export const LinkedInCampaign = observer(
  ({
    job,
    promotions,
    onStepEvent,
    onClose,
    progress,
    linkedInContractForm,
    vonqCampaignForm,
    linkedInCampaignForm,
  }: StepParams) => {
    const categoriesOptions: SelectOption[] = sorted(
      CategoriesOptions.map((option) => ({ label: option.name, value: option.id }))
    )
    const industriesOptions: SelectOption[] = sorted(
      IndustriesOptions.map((option) => ({ label: option.name, value: option.id }))
    )

    const promotion = promotions.filter({ provider: Provider.Vonq })[0]
    const hasContract: boolean = promotion?.attributes.data !== undefined

    const { running, error, value, performAction } = useAsyncAction(
      async () => {
        const data: VonqData = hasContract
          ? promotion!.attributes.data!
          : {
              contract: {
                channel_id: LINKEDIN_CHANNEL_ID,
                followed_instructions: false,
                credentials: linkedInContractForm.data(),
              },
            }
        data.campaign = vonqCampaignForm.data() as VonqCampaignData
        data.channelRequirements = linkedInCampaignForm.data()
        data.channelRequirements.categories = [data.channelRequirements.categories]
        data.channelRequirements.industries = [data.channelRequirements.industries]

        if (hasContract) {
          await promotion!.update({
            ats_job_posting_id: job.id,
            provider: Provider.Vonq,
            data: data as PromotionData,
          })
        } else {
          await promotions.create({
            ats_job_posting_id: job.id,
            provider: Provider.Vonq,
            data,
          })
        }
        return onStepEvent(StepEvent.Stop)
      },
      {
        errorFlash: true,
        successFlash: true,
      }
    )

    useEffect(() => {
      if (error) {
        if (value instanceof Errors) {
          linkedInContractForm.setErrors(value)
          vonqCampaignForm.setErrors(value)
          linkedInCampaignForm.setErrors(value)

          if (linkedInContractForm.hasErrors) {
            onStepEvent(StepEvent.Restart)
          } else if (vonqCampaignForm.hasErrors) {
            onStepEvent(StepEvent.Back)
          }
        }
      }
    }, [error]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Modal
        title={i18n.t('ats.job_details.promotion.vonq.linkedin.campaign_step.title')}
        description={i18n.t('ats.job_details.promotion.vonq.linkedin.campaign_step.description', {
          step: progress.amount,
        })}
        illustration="document-edit"
        onClose={onClose}
        footer={
          <Box width="full" flexDirection="row" justifyContent="spaceBetween">
            <Button disabled={false} onClick={() => onStepEvent(StepEvent.Back)}>
              {i18n.t('ats.job_details.promotion.vonq.linkedin.campaign_step.back')}
            </Button>
            <Button disabled={!linkedInCampaignForm.isComplete || running} onClick={performAction}>
              {i18n.t('ats.job_details.promotion.vonq.linkedin.campaign_step.submit')}
            </Button>
          </Box>
        }
        progress={progress}
      >
        <Box gap="s16" flexDirection="column">
          <Select
            label={i18n.t(
              'ats.job_details.promotion.vonq.linkedin.campaign_step.fields.categories'
            )}
            options={categoriesOptions}
            field={linkedInCampaignForm.get('categories')}
            mandatory
            none={i18n.t('common.choose')}
          />
          <Select
            label={i18n.t(
              'ats.job_details.promotion.vonq.linkedin.campaign_step.fields.industries'
            )}
            options={industriesOptions}
            field={linkedInCampaignForm.get('industries')}
            mandatory
            none={i18n.t('common.choose')}
          />
        </Box>
      </Modal>
    )
  }
)
